import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { getClient, getProjectInfo, toneOf } from '../lib/supabase';
import { EnvStrip } from '../components/EnvStrip';
import { rememberEnv } from '../lib/env';

type LoginStep = 'credentials' | 'mfa' | 'magic_sent';

export function Login() {
  // `env` comes from AuthProvider, which resolved it from the path or from the
  // remembered choice. /login carries no env prefix, so this is the only honest
  // source: signing in has to hit the same project the session will belong to.
  const { env, login, verifyMfa, mfaRequired } = useAuth();
  const authClient = getClient(env);
  // Which project this sign-in will actually authenticate against.
  //
  // Added 2026-08-22 after a crossed-environment login: two projects, one login
  // screen, and nothing on it said which was which — so "signed in, got bounced,
  // signed in again" gave the operator no way to tell Proto from Stage. Sessions
  // and users are per-project, so this is the screen where the ambiguity costs
  // the most, and it was the only surface without a ProjectBanner. The ref is
  // parsed from the configured URL, so it cannot drift from what the build does.
  const project = getProjectInfo(env);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  // Separate loading state per form so when one button is in flight the
  // other doesn't appear stuck. Previously a single shared `loading` state
  // made BOTH the password "Signing in..." and the magic-link "Sending..."
  // buttons display their loading text simultaneously when either was
  // in flight — visually identical to a both-hung failure mode.
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [magicLoading, setMagicLoading]       = useState(false);
  const [mfaLoading, setMfaLoading]           = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordLoading(true);
    const { error } = await login(email, password);
    if (error) setError(error);
    setPasswordLoading(false);
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMfaLoading(true);
    const { error } = await verifyMfa(mfaCode);
    if (error) setError(error);
    setMfaLoading(false);
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Enter your email address above first.'); return; }
    setError('');
    setMagicLoading(true);
    if (!authClient) { setError('Authentication service not configured.'); setMagicLoading(false); return; }
    const { error } = await authClient.auth.signInWithOtp({
      email,
      options: {
        // Redirect back to our callback page after clicking the link.
        //
        // `?env=` is load-bearing, not decoration. The link is minted by ONE
        // project and its token can only be verified by that project's client.
        // The callback route carries no env in its path, and the link is opened
        // by whatever browser the mail client hands it to — often not this one,
        // so localStorage is not available to say which project we meant. The
        // env therefore rides in the URL. Without it the callback guesses, and a
        // wrong guess fails as "unrecognized JWT kid ... for algorithm ES256",
        // which reads like a broken account rather than a crossed environment.
        emailRedirectTo: `${window.location.origin}/auth/callback?env=${env}`,
      },
    });
    setMagicLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setStep('magic_sent');
    }
  };

  // Magic link sent confirmation
  if (step === 'magic_sent') {
    return (
      <div className="min-h-screen bg-surface-light flex items-center justify-center px-4">
        <EnvStrip fixed />
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-orange mb-4">
              <span className="text-white text-xl font-bold">V</span>
            </div>
            <h1 className="text-2xl font-bold text-near-black tracking-tight">Check your email</h1>
            <p className="text-sm text-gray-text mt-1">Magic link sent</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-border p-8 text-center space-y-4">
            <div className="text-4xl">📬</div>
            <p className="text-sm text-near-black">
              We sent a sign-in link to <span className="font-semibold">{email}</span>.
            </p>
            <p className="text-sm text-gray-text">
              Click the link in the email and you'll be signed in automatically. The link expires in 1 hour.
            </p>
            <button
              onClick={() => { setStep('credentials'); setError(''); }}
              className="text-sm text-brand-orange hover:underline"
            >
              Back to login
            </button>
          </div>
          <p className="text-xs text-gray-text text-center mt-6">Sacramento Pilot v1.0</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-light flex items-center justify-center px-4">
        <EnvStrip fixed />
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-orange mb-4">
            <span className="text-white text-xl font-bold">V</span>
          </div>
          <h1 className="text-2xl font-bold text-near-black tracking-tight">VOTD Admin</h1>
          <p className="text-sm text-gray-text mt-1">
            {mfaRequired ? 'Enter your authenticator code' : 'Sign in to your account'}
          </p>
          <p className="text-xs text-gray-text mt-1">
            <span className={`font-semibold ${{ prod: 'text-red-700', devstage: 'text-green-700', unknown: 'text-amber-700' }[toneOf(project.ref)]}`}>
              {project.name}
            </span>
            {' · '}
            <span className="font-mono">{project.ref ?? 'NOT CONFIGURED'}</span>
          </p>
          {env !== 'stage' && (
            // Opening the dev-stage tab with no dev-stage session lands here. Prod's session is still
            // saved, so this goes straight back to Prod without signing in again.
            <p className="text-xs mt-2">
              <a
                href="/stage/dashboard"
                onClick={() => rememberEnv('stage')}
                className="text-gray-text hover:text-near-black underline"
              >
                ← Back to Prod
              </a>
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
          {mfaRequired ? (
            /* ── MFA step ── */
            <form onSubmit={handleMfa} className="space-y-5">
              <div className="flex items-center gap-3 p-3 bg-surface-light rounded-lg mb-2">
                <span className="text-2xl">🔐</span>
                <p className="text-sm text-gray-text">
                  Open your authenticator app and enter the 6-digit code.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-near-black mb-1.5">
                  Authenticator code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-border text-sm text-near-black placeholder-gray-text text-center tracking-widest text-lg font-mono focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition"
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={mfaLoading || mfaCode.length !== 6}
                className="w-full py-2.5 rounded-lg bg-brand-orange text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition"
              >
                {mfaLoading ? 'Verifying…' : 'Verify'}
              </button>
            </form>
          ) : (
            /* ── Credentials step ── */
            <div className="space-y-5">
              {/* Email field — shared between password login and magic link */}
              <div>
                <label className="block text-sm font-medium text-near-black mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@votd.app"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-border text-sm text-near-black placeholder-gray-text focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition"
                />
              </div>

              {/* Password form */}
              <form onSubmit={handleLogin} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-near-black mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-border text-sm text-near-black placeholder-gray-text focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition"
                  />
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="w-full py-2.5 rounded-lg bg-brand-orange text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition"
                >
                  {passwordLoading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-gray-text">or</span>
                <div className="flex-1 border-t border-border" />
              </div>

              {/* Magic link option */}
              <form onSubmit={handleMagicLink}>
                <button
                  type="submit"
                  disabled={magicLoading}
                  className="w-full py-2.5 rounded-lg border border-border text-near-black text-sm font-medium hover:bg-surface-light disabled:opacity-50 transition"
                >
                  {magicLoading ? 'Sending…' : 'Send magic link instead'}
                </button>
              </form>
              <p className="text-xs text-gray-text text-center -mt-2">
                No password needed — we'll email you a one-click sign-in link.
              </p>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-text text-center mt-6">Sacramento Pilot v1.0</p>
      </div>
    </div>
  );
}
